import React, { useState } from 'react';
import { DocumentIcon, PhotoIcon, ArrowDownTrayIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TicketAttachment } from '../../types/customerTicket';
import { customerTicketAttachmentService } from '../../services/customerTicketAttachmentService';

interface AttachmentListProps {
  attachments: TicketAttachment[];
  onDelete?: (attachment: TicketAttachment) => void;
  canDelete?: boolean;
}

const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  onDelete,
  canDelete = false
}) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<TicketAttachment | null>(null);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <PhotoIcon className="h-5 w-5 text-blue-500" />;
    }
    return <DocumentIcon className="h-5 w-5 text-gray-500" />;
  };

  const isImage = (fileType: string) => {
    return fileType.startsWith('image/');
  };

  const handleImageClick = (attachment: TicketAttachment) => {
    if (isImage(attachment.type)) {
      setLightboxImage(attachment);
      setLightboxOpen(true);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Mellékletek ({attachments.length}):
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {attachments.map((attachment) => (
          <div
            key={attachment.id}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary-500 dark:hover:border-primary-500 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Icon or Thumbnail */}
              {isImage(attachment.type) ? (
                <div 
                  className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => handleImageClick(attachment)}
                  title="Kattints a nagyításhoz"
                >
                  <img
                    src={attachment.url}
                    alt={attachment.name}
                    className="h-12 w-12 object-cover rounded"
                  />
                </div>
              ) : (
                <div className="flex-shrink-0">
                  {getFileIcon(attachment.type)}
                </div>
              )}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {attachment.name}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{customerTicketAttachmentService.formatFileSize(attachment.size)}</span>
                  <span>•</span>
                  <span>{attachment.uploadedByName}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 ml-2">
              {/* Download */}
              <a
                href={attachment.url}
                download={attachment.name}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="Letöltés"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </a>

              {/* Delete */}
              {canDelete && onDelete && (
                <button
                  onClick={() => onDelete(attachment)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Törlés"
                  type="button"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && lightboxImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors"
            title="Bezárás"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
          
          <div className="max-w-7xl max-h-full flex flex-col items-center">
            <img
              src={lightboxImage.url}
              alt={lightboxImage.name}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 text-center">
              <p className="text-white text-lg font-medium">{lightboxImage.name}</p>
              <p className="text-gray-300 text-sm mt-1">
                {customerTicketAttachmentService.formatFileSize(lightboxImage.size)}
              </p>
              <a
                href={lightboxImage.url}
                download={lightboxImage.name}
                className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Letöltés
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentList;
